<template>
  <el-dialog
    :visible="true"
    :fullscreen="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
  >
    <div slot="title">
      {{ title }}
      <el-tag
        v-if="customerSale.status && (type === 'update' || type === 'view')"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <el-alert
      v-if="customerSale.status === 'done' && type !== 'view'"
      title="Ez a vevői eladás már le lett zárva, ezért csak a számlázhatóság, megjegyzés és megrendelés szamok módosíthatók."
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 30px; margin-top: -20px;"
    />
    <div v-if="type === 'view' && customerSale.receipt_number">
      <el-link @click="openReceipt" type="primary" style="margin-bottom: 30px; margin-top: -20px;">
        <i class="el-icon-printer el-icon--right"></i>
        {{ customerSale.receipt_number }}
      </el-link>
    </div>
    <div v-loading="loading">
      <el-form
        ref="saleForm"
        label-position="top"
        :model="customerSale"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="supplier"
              label="Szállító"
            >
              <el-select
                v-model="customerSale.supplier"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="customerSale.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="supplier in suppliers"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="supplier.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Szállitó raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Ebből a raktárból küldjük az árut. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                    placement="right"
                  >
                    <i class="el-icon-info"/>
                  </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="customerSale.storage"
                style="width: 100%;"
                value-key="id"
                :disabled="customerSale.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer"
              label="Vevő"
            >
              <el-select
                v-model="customerSale.customer"
                style="width: 100%;"
                value-key="id"
                filterable
                remote
                :remote-method="searchPartners"
                :loading="partnerLoading"
                placeholder="Írd be a partner nevét a kereséshez..."
                :disabled="customerSale.status !== 'recorded' || type === 'view'"
                @change="onCustomerChange"
              >
                <el-option
                  v-for="customer in customers"
                  :key="customer.id + 'customer'"
                  :value="customer"
                  :label="`#${customer.id} ${customer.company.name}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer_address"
              label="Vevő címe"
            >
              <el-select
                v-model="customerSale.customer_address"
                v-loading="addressesLoading"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="customerSale.status !== 'recorded' || addresses.length === 0 || type === 'view'"
              >
                <el-option
                  v-for="address in addresses"
                  :key="address.id + 'address'"
                  :value="address"
                  :label="`${address.full_address}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20" style="display: flex; align-items: center">
          <el-col :span="6">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="currency_id"
              label="Deviza"
            >
              <el-select
                v-model="customerSale.currency"
                :disabled="customerSale.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="currency in currencies"
                  :key="currency.id + 'currency'"
                  :value="currency"
                  :label="currency.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="delivery_date"
              label="Szállítás dátuma"
            >
              <el-date-picker
                :key="dateKey"
                v-model="customerSale.delivery_date"
                type="date"
                :disabled="customerSale.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
                @input="dateKey++"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              label=""
              prop="is_billable"
              style="margin-bottom: 0"
            >
              <el-checkbox
                v-model="customerSale.is_billable"
                :disabled="type === 'view'"
              >
                Számlázandó
              </el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">

          <el-col :span="12">
            <el-form-item
              prop="order_numbers"
              label="Megrendelés számok"
            >
              <el-input
                v-model="customerSale.order_numbers"
                type="textarea"
                :rows="2"
                style="word-break: normal;"
                :disabled="type === 'view'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              label="Megjegyzés"
              prop="comment"
            >
              <el-input
                v-model="customerSale.comment"
                type="textarea"
                :rows="2"
                style="word-break: normal;"
                :disabled="type === 'view'"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <p-table
          v-if="customerSale && type === 'view' || (customerSale.status === 'done')"
          ref="productItems"
          style="margin-bottom: 20px;"
          :customer-id="customerSale && customerSale.customer && customerSale.customer.id"
          :emptyLine="emptyLine"
          :columns="columns"
          :opts="tableOptions"
          dialog="CustomerSaleDialog"
          :endpoint="customerSale && customerSale.id ? `api/customer-sales/${customerSale.id}/items` : null"
          @change="onPriceChange"
        />
        <product-items
          v-if="type !== 'view' && customerSale.status !== 'done'"
          ref="productItems"
          :default-items="customerSale.order_items"
          :currency="customerSale.currency && customerSale.currency.code"
          :is-product-select-from-stock="customerSale.status !== 'done'"
          :load-base-count-from-stock="false"
          :enabled-elements="enabledElements"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'supplierSelector'] : ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions', 'supplierSelector']"
          compare-type="completion"
          :compare-items="customerSale.sold_items && customerSale.sold_items.length ? customerSale.sold_items : null"
          :storage-id="customerSale.storage && customerSale.storage.id"
          @priceChange="onPriceChange"
        />
      </el-form>
      <div class="price-sum">
        <div>Nettó: <strong>{{ customerSale.net_price | currency(customerSale.currency.code) }}</strong></div>
        <div style="margin-left: 20px;">
          Bruttó: <strong>{{ customerSale.gross_price | currency(customerSale.currency.code) }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        v-if="customerSale.status !== 'recorded' && customerSale.receipt_file"
        @click="openReceipt"
      >
        Bizonylat nyomtatása
      </el-button>
      <el-button
        type="primary"
        :loading="buttonLoading"
        @click="store"
      >
        Mentés
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import ProductItems from '../../utils/ProductItems/index';
import PTable from '../../utils/PTable/index'

export default {
  components: {
    ProductItems,
    PTable,
  },
  props: ['type', 'customerSaleId'],
  data() {
    return {
      columns: [
        {
          label: 'Termék',
          prop: 'name',
          minWidth: 200
        },
        {
          label: 'Beszállító',
          prop: 'supplier_name',
          showTooltip: true
        },
        {
          label: 'Szavatossági idő',
          prop: 'expiration_date',
          minWidth: 110
        },
        {
          label: 'Mennyiség',
          prop: 'count',
          align: 'right'
        },
        {
          label: 'Kiszerelés',
          prop: 'pack_type_name',
          align: 'right'
        },
        {
          label: 'Egységár',
          prop: 'unit_price',
          align: 'right',
          format: 'currency'
        },
        {
          label: 'Nettó',
          prop: 'net_price',
          align: 'right',
          format: 'currency'
        },
        {
          label: 'Bruttó',
          prop: 'gross_price',
          align: 'right',
          format: 'currency'
        },
        {
          label: 'Áfa',
          prop: 'vat',
          align: 'right',
          format: 'percent'
        },
      ],
      emptyLine: {
        count: 1,
        unit_price: 0,
        net_price: 0,
        count_per_pack: 1,
        vat: 27,
        expiration_date: null,
        comment: '',
        name: '',
        product_pack_type_id: null,
        product_id: null,
        supplier_id: null,
        customer_sale_id: this.customerSale && this.customerSale.id,
        vat_value: 0,
        gross_price: 0,
        supplier_name: null,
      },
      loading: false,
      addressesLoading: false,
      receiptPrefix: '',
      buttonLoading: false,
      partnerLoading: false,
      addresses: [],
      customers: [],
      dateKey: 0,
      customerSale: {
        status: 'recorded',
        net_price: 0,
        gross_price: 0,
        order_items: [],
        storage: {},
        customer: null,
        supplier: {},
        completed_at: null,
        is_billable: true,
        comment: '',
        order_numbers: '',
        currency: {},
      },
      rules: {
        customer: [
          {
            required: true,
            message: 'Vevő kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        supplier: [
          {
            required: true,
            message: 'Beszállító kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        completed_at: [
          {
            required: true,
            message: 'Teljesítés megadása kötelező',
            trigger: 'change'
          }
        ],
        currency: [
          {
            required: true,
            message: 'Deviza megadása kötelező',
            trigger: 'change'
          }
        ],
      },
    };
  },
  computed: {
    tableOptions() {
      return {
        currency: this.customerSale && this.customerSale.currency && this.customerSale.currency.code || 'HUF',
        addItem: false,
        updateItem: false,
        deleteItem: false,
        editableFields: [],
      };
    },
    title() {
      if (this.type === 'add') {
        return 'Új vevői eladás';
      } else if (this.type === 'update') {
        return 'Vevői eladás módosítása';
      } else if (this.type === 'view') {
        return 'Vevői eladás megtekintése';
      } else {
        return '';
      }
    },
    buttonText() {
      if (this.type === 'add') {
        return 'Hozzáadása';
      } else if (this.type === 'update') {
        return 'Módosítás';
      } else {
        return '';
      }
    },
    enabledElements() {
      if (this.type === 'view') {
        return [];
      }
      switch (this.customerSale.status) {
        case 'recorded':
          return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDateSelector', 'supplierSelector'];
        case 'done':
          return [];
        default:
          return [];
      }
    },
    suppliers() {
      return this.$store.getters['ownCompany/ownCompanies'];
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
    currencies() {
      return this.$store.getters['currency/currencies'];
    },
  },
  async mounted() {
    this.loading = true;
    const requests = [];
    if (!this.currencies || !this.currencies.length) {
      requests.push(this.$store.dispatch('currency/fetchAll'));
    }
    if (!this.storages || !this.storages.length) {
      requests.push(this.$store.dispatch('storage/fetchAll'));
    }
    if (!this.suppliers || !this.suppliers.length) {
      requests.push(this.$store.dispatch('ownCompany/fetchAll'));
    }
    await Promise.all(requests);
    this.receiptPrefix = (await axios.get('api/receipt-prefixes/customerSale')).full_prefix;
    if (this.customerSaleId) {
      this.customerSale = await this.$store.dispatch('customerSale/fetch', this.customerSaleId);
      this.customers.push(this.customerSale.customer);
      this.addresses = this.customerSale.customer.company.addresses.filter(a => a.pivot.type === 'delivery');
      if (this.type === 'add') {
        this.customerSale.status = 'recorded';
        this.customerSale.receipt_number = null;
        this.customerSale.order_items = this.customerSale.order_items.map(item => {
          delete item.id;
          return item;
        });
      }
    } else {
      this.customerSale.supplier = this.suppliers[0];
      this.customerSale.currency = this.currencies[0];
      this.customerSale.storage = this.storages[0];
      this.customerSale.delivery_date = moment()
        .add(3, 'days')
        .format('Y-MM-DD');
    }
    this.loading = false;
  },
  methods: {
    async searchPartners(query) {
      this.partnerLoading = true;
      if (!query) {
        this.customers = [];
      } else {
        const queryString = new URLSearchParams({
          name: query,
        });
        this.customers = await this.$store.dispatch('partner/search', '?' + queryString.toString());
      }
      this.partnerLoading = false;
    },
    async onCustomerChange() {
      this.addressesLoading = true;
      this.addresses = await axios.get(`api/addresses/company/${this.customerSale.customer.company.id}/delivery`);
      if (this.addresses) {
        this.customerSale.customer_address = this.addresses[0];
      }
      this.addressesLoading = false;
    },
    onPriceChange({ netPrice, grossPrice }) {
      this.customerSale.net_price = netPrice;
      this.customerSale.gross_price = grossPrice;
    },
    handleClose() {
      if (this.type === 'view') {
        return this.$router.replace('/sales/customer-sales');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
        .then(() => {
          this.$router.replace('/sales/customer-sales');
        })
        .catch(() => {
        });
    },

    async store() {
      this.$refs.saleForm.validate(async valid => {
        if (valid) {
          if (this.customerSale.status !== 'done') {
            this.customerSale.order_items = this.$refs['productItems'].getItems();
            if (!this.customerSale.order_items.length) {
              return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
            } else if (!this.$refs['productItems'].validateProducts()) {
              return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
            }
          }
          this.buttonLoading = true;
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('customerSale/create', this.customerSale);
            } else if (this.type === 'update') {
              await this.$store.dispatch('customerSale/update', this.customerSale);
            }
            this.$store.dispatch('customerSale/fetchAll');
            this.buttonLoading = false;
            this.$router.replace('/sales/customer-sales');
          } catch (e) {
          }
          this.buttonLoading = false;
        }
      });
    },
    openReceipt() {
      if (this.customerSale.receipt_file && this.customerSale.receipt_file.path) {
        window.open(this.customerSale.receipt_file.path);
      }
    },
    getStatus() {
      switch (this.customerSale.status) {
        case 'recorded':
          return {
            label: 'Rögzítve',
            tag: 'info',
          };
        case 'done':
          return {
            label: 'Teljesítve',
            tag: 'success',
          };
      }
    },
  },
};
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;

    > div {
      display: inline-block;
    }
  }
</style>
